import { useEffect, useState, type FormEvent } from 'react';
import { apiFetch } from '../auth/session';

type FiscalProfile = {
  store_id: number;
  business_name: string;
  legal_address: string | null;
  legal_city: string | null;
  legal_zip: string | null;
  legal_province: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  vat_number: string | null;
  ritenuta_percent: number | null;
  split_payment: boolean;
  fiscal_code: string | null;
  sdi_code: string | null;
  pec: string | null;
  billing_email: string | null;
};

const EMPTY_FORM = {
  business_name: '',
  legal_address: '',
  legal_city: '',
  legal_zip: '',
  legal_province: '',
  country: 'IT',
  phone: '',
  email: '',
  vat_number: '',
  ritenuta_percent: '',
  split_payment: false,
  fiscal_code: '',
  sdi_code: '',
  pec: '',
  billing_email: '',
};

export function FiscalProfileCard() {
  const [profile, setProfile] = useState<FiscalProfile | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const isItalian = form.country === 'IT';

  async function load() {
    try {
      const res = await apiFetch('/api/v1/tenant/billing/profile');
      if (!res.ok) {
        if (res.status === 404) {
          setLoading(false);
          return;
        }
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      const body = (await res.json()) as { profile: FiscalProfile };
      const p = body.profile;
      setProfile(p);
      setForm({
        business_name: p.business_name || '',
        legal_address: p.legal_address || '',
        legal_city: p.legal_city || '',
        legal_zip: p.legal_zip || '',
        legal_province: p.legal_province || '',
        country: p.country || 'IT',
        phone: p.phone || '',
        email: p.email || '',
        vat_number: p.vat_number || '',
        ritenuta_percent: p.ritenuta_percent != null ? String(p.ritenuta_percent) : '',
        split_payment: p.split_payment,
        fiscal_code: p.fiscal_code || '',
        sdi_code: p.sdi_code || '',
        pec: p.pec || '',
        billing_email: p.billing_email || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function onCountryChange(country: string) {
    setForm((f) => ({
      ...f,
      country,
      // Reset province for foreign
      legal_province: country === 'IT' ? f.legal_province : 'EE',
      // Reset SDI for foreign
      sdi_code: country === 'IT' ? f.sdi_code : 'XXXXXXX',
    }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        business_name: form.business_name.trim(),
        legal_address: form.legal_address.trim() || null,
        legal_city: form.legal_city.trim() || null,
        legal_zip: form.legal_zip.trim() || null,
        legal_province: form.legal_province.trim() || null,
        country: form.country,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        vat_number: form.vat_number.trim() || null,
        ritenuta_percent: form.ritenuta_percent ? Number(form.ritenuta_percent) : null,
        split_payment: form.split_payment,
        fiscal_code: form.fiscal_code.trim() || null,
        sdi_code: form.sdi_code.trim() || null,
        pec: form.pec.trim() || null,
        billing_email: form.billing_email.trim() || null,
      };

      // Italian validation
      if (form.country === 'IT') {
        if (!form.business_name.trim()) throw new Error('Ragione sociale obbligatoria');
        if (!form.vat_number.trim()) throw new Error('Partita IVA obbligatoria per clienti italiani');
        if (!form.fiscal_code.trim()) throw new Error('Codice Fiscale obbligatorio per clienti italiani');
        if (!form.sdi_code.trim() && !form.pec.trim()) {
          throw new Error('Codice Destinatario SDI o PEC obbligatorio per fatturazione elettronica');
        }
      }

      const res = await apiFetch('/api/v1/tenant/billing/profile', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string; profile?: FiscalProfile };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (body.profile) setProfile(body.profile);
      setMessage('Dati di fatturazione salvati');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <article className="sk-saas-card">
        <h2 className="sk-saas-card__title">Dati di Fatturazione</h2>
        <p className="sk-saas-hint">Caricamento…</p>
      </article>
    );
  }

  const inputStyle = { maxWidth: '100%' };

  return (
    <article className="sk-saas-card">
      <h2 className="sk-saas-card__title">
        Dati di Fatturazione
        {profile?.vat_number ? (
          <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--sk-success, #16a34a)', marginLeft: '0.5rem' }}>
            ✓ Configurato
          </span>
        ) : null}
      </h2>
      <p className="sk-saas-card__sub">
        Dati fiscali per fatturazione elettronica (SDI/PEC) — obbligatori per clienti italiani.
      </p>

      {error ? <p className="sk-error">{error}</p> : null}
      {message ? <p className="sk-ok">{message}</p> : null}

      <form className="sk-saas-form" style={{ marginTop: '0.85rem' }} onSubmit={onSubmit}>
        {/* Country switch */}
        <label className="sk-field">
          <span>Nazione</span>
          <select
            className="sk-input"
            value={form.country}
            disabled={busy}
            onChange={(e) => onCountryChange(e.target.value)}
            style={inputStyle}
          >
            <option value="IT">Italia</option>
            <option value="DE">Germania</option>
            <option value="FR">Francia</option>
            <option value="ES">Spagna</option>
            <option value="GB">Regno Unito</option>
            <option value="US">Stati Uniti</option>
            <option value="CH">Svizzera</option>
            <option value="AT">Austria</option>
            <option value="NL">Olanda</option>
            <option value="BE">Belgio</option>
            <option value="PT">Portogallo</option>
            <option value="IE">Irlanda</option>
            <option value="PL">Polonia</option>
            <option value="RO">Romania</option>
            <option value="EE">Altro (Extra-UE)</option>
          </select>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
          {/* Ragione Sociale */}
          <label className="sk-field">
            <span>Ragione Sociale {isItalian ? '*' : ''}</span>
            <input
              className="sk-input"
              value={form.business_name}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
              placeholder="Mario Rossi S.r.l."
              style={inputStyle}
            />
          </label>

          {/* Partita IVA / VAT */}
          <label className="sk-field">
            <span>Partita IVA / VAT {isItalian ? '*' : ''}</span>
            <input
              className="sk-input"
              value={form.vat_number}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, vat_number: e.target.value }))}
              placeholder={isItalian ? '11 cifre' : 'es. DE123456789'}
              style={inputStyle}
            />
          </label>

          {/* Codice Fiscale — solo IT */}
          {isItalian ? (
            <label className="sk-field">
              <span>Codice Fiscale *</span>
              <input
                className="sk-input"
                value={form.fiscal_code}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, fiscal_code: e.target.value }))}
                placeholder="11 o 16 caratteri"
                style={inputStyle}
              />
            </label>
          ) : null}

          {/* SDI — solo IT */}
          {isItalian ? (
            <label className="sk-field">
              <span>Codice Destinatario SDI</span>
              <input
                className="sk-input"
                value={form.sdi_code}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, sdi_code: e.target.value }))}
                placeholder="7 caratteri (es. ABC1234)"
                maxLength={7}
                style={inputStyle}
              />
            </label>
          ) : (
            <label className="sk-field">
              <span>Codice Destinatario</span>
            <input
              className="sk-input"
              value="XXXXXXX"
              disabled
              style={inputStyle}
            />
            </label>
          )}

          {/* PEC — solo IT */}
          {isItalian ? (
            <label className="sk-field">
              <span>PEC (alternativa a SDI)</span>
              <input
                className="sk-input"
                type="email"
                value={form.pec}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, pec: e.target.value }))}
                placeholder="azienda@pec.it"
                style={inputStyle}
              />
            </label>
          ) : null}

          {/* Email fatturazione */}
          <label className="sk-field">
            <span>Email fatturazione (copia PDF)</span>
            <input
              className="sk-input"
              type="email"
              value={form.billing_email}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, billing_email: e.target.value }))}
              placeholder="fatture@azienda.it"
              style={inputStyle}
            />
          </label>
        </div>

        {/* Indirizzo sede legale */}
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem' }}>
          Sede Legale
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.75rem' }}>
          <label className="sk-field">
            <span>Indirizzo</span>
            <input
              className="sk-input"
              value={form.legal_address}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, legal_address: e.target.value }))}
              placeholder="Via Roma 10"
              style={inputStyle}
            />
          </label>
          <label className="sk-field">
            <span>CAP</span>
            <input
              className="sk-input"
              value={form.legal_zip}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, legal_zip: e.target.value }))}
              placeholder="20121"
              style={inputStyle}
            />
          </label>
          <label className="sk-field">
            <span>Comune</span>
            <input
              className="sk-input"
              value={form.legal_city}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, legal_city: e.target.value }))}
              placeholder="Milano"
              style={inputStyle}
            />
          </label>
          <label className="sk-field">
            <span>Provincia</span>
            <input
              className="sk-input"
              value={form.legal_province}
              disabled={busy || !isItalian}
              onChange={(e) => setForm((f) => ({ ...f, legal_province: e.target.value.toUpperCase() }))}
              placeholder={isItalian ? 'MI' : 'EE'}
              maxLength={2}
              style={inputStyle}
            />
          </label>
        </div>

        {/* Contatti */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
          <label className="sk-field">
            <span>Telefono</span>
            <input
              className="sk-input"
              value={form.phone}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+39 02 1234567"
              style={inputStyle}
            />
          </label>
          <label className="sk-field">
            <span>Email ordinaria</span>
            <input
              className="sk-input"
              type="email"
              value={form.email}
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="info@azienda.it"
              style={inputStyle}
            />
          </label>
        </div>

        {/* Ritenuta + Split payment — solo IT */}
        {isItalian ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <label className="sk-field">
              <span>Ritenuta d'acconto (%)</span>
              <input
                className="sk-input"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.ritenuta_percent}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, ritenuta_percent: e.target.value }))}
                placeholder="0"
                style={inputStyle}
              />
            </label>
            <label className="sk-field" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.split_payment}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, split_payment: e.target.checked }))}
              />
              <span>Split payment (PA)</span>
            </label>
          </div>
        ) : null}

        <div className="sk-saas-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="sk-btn" type="submit" disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva dati fatturazione'}
          </button>
        </div>

        {isItalian ? (
          <p className="sk-saas-hint" style={{ marginTop: '0.75rem' }}>
            * Campi obbligatori per la fatturazione elettronica italiana.
            Codice Destinatario SDI <strong>oppure</strong> PEC devono essere compilati.
          </p>
        ) : (
          <p className="sk-saas-hint" style={{ marginTop: '0.75rem' }}>
            Per clienti esteri: SDI = <code>XXXXXXX</code>, Provincia = <code>EE</code>.
            Codice Fiscale e PEC non sono richiesti.
          </p>
        )}
      </form>
    </article>
  );
}
